/*
 * Calendar math in the browser's local zone. Ranges are picked and data is rendered in local
 * time; `toISOString()` converts the resulting instants to UTC for the backend.
 */
import { MONTHS_SHORT } from './utcDate';

export { isValidDate, MONTHS_SHORT, WEEKDAYS_SHORT } from './utcDate';

const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

export const localOf = (
  year: number,
  month: number,
  day: number,
  hours = 0,
  minutes = 0,
  seconds = 0,
  ms = 0,
): Date => new Date(year, month, day, hours, minutes, seconds, ms);

const daysInMonth = (year: number, month: number): number => new Date(year, month + 1, 0).getDate();

export const startOfDay = (d: Date): Date => localOf(d.getFullYear(), d.getMonth(), d.getDate());

/** 1 ms before the next start, so an hour repeated by a DST change at midnight stays inside. */
export const endOfDay = (d: Date): Date =>
  new Date(localOf(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime() - 1);

/** By calendar date, not by 24h steps, so DST days stay one day long. */
export const addDays = (d: Date, n: number): Date => {
  const next = new Date(d.getTime());
  next.setDate(next.getDate() + n);
  return next;
};

/** Built from the civil date, so a DST gap at midnight cannot move Monday's start to 01:00. */
export const startOfWeek = (d: Date): Date => {
  const mondayOffset = (d.getDay() + 6) % 7;
  return localOf(d.getFullYear(), d.getMonth(), d.getDate() - mondayOffset);
};

export const endOfWeek = (d: Date): Date => endOfDay(addDays(startOfWeek(d), 6));

export const startOfMonth = (d: Date): Date => localOf(d.getFullYear(), d.getMonth(), 1);

export const endOfMonth = (d: Date): Date =>
  new Date(localOf(d.getFullYear(), d.getMonth() + 1, 1).getTime() - 1);

export const startOfYear = (d: Date): Date => localOf(d.getFullYear(), 0, 1);

export const endOfYear = (d: Date): Date =>
  new Date(localOf(d.getFullYear() + 1, 0, 1).getTime() - 1);

/** Clamps the day, so 31 Jan + 1 month is 28 Feb rather than spilling into March. */
export const addMonths = (d: Date, n: number): Date => {
  const target = new Date(d.getFullYear(), d.getMonth() + n, 1);
  const year = target.getFullYear();
  const month = target.getMonth();

  return localOf(
    year,
    month,
    Math.min(d.getDate(), daysInMonth(year, month)),
    d.getHours(),
    d.getMinutes(),
    d.getSeconds(),
    d.getMilliseconds(),
  );
};

export const addYears = (d: Date, n: number): Date => addMonths(d, n * 12);

/** ISO 8601 week number, 1..53, by the Thursday rule. */
export const isoWeekNumber = (d: Date): number => {
  const thursday = startOfDay(d);
  const isoDay = thursday.getDay() || 7;
  thursday.setDate(thursday.getDate() + 4 - isoDay);

  // Counted on UTC midnights of the same civil dates, so a DST shift cannot skew the division.
  const dayIndex = Date.UTC(thursday.getFullYear(), thursday.getMonth(), thursday.getDate());
  const yearStart = Date.UTC(thursday.getFullYear(), 0, 1);
  return Math.ceil(((dayIndex - yearStart) / 86_400_000 + 1) / 7);
};

export const isSameDay = (a: Date, b: Date): boolean =>
  startOfDay(a).getTime() === startOfDay(b).getTime();

export const isSameWeek = (a: Date, b: Date): boolean =>
  startOfWeek(a).getTime() === startOfWeek(b).getTime();

export const isSameMonth = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();

export const isSameYear = (a: Date, b: Date): boolean => a.getFullYear() === b.getFullYear();

/** 6 rows x 7 local midnights, Monday-first, padded with adjacent-month days. */
export const monthGrid = (cursor: Date): Date[][] => {
  const gridStart = startOfWeek(startOfMonth(cursor));

  return Array.from({ length: 6 }, (_, row) =>
    Array.from({ length: 7 }, (_, col) => addDays(gridStart, row * 7 + col)),
  );
};

export const formatDayMonth = (d: Date): string => `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;

export const formatDayMonthYear = (d: Date): string => `${formatDayMonth(d)} ${d.getFullYear()}`;

export const formatMonthYear = (d: Date): string =>
  `${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;

export const formatLongMonthYear = (d: Date): string =>
  `${MONTHS_LONG[d.getMonth()]} ${d.getFullYear()}`;
